import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { paymentAPI } from '../../api/payment.api';
import { useLanguage } from '../../context/LanguageContext';
import './CheckoutResult.css';

const MAX_STATUS_CHECKS = 10;
const STATUS_CHECK_DELAY = 3000;

const CheckoutResult = ({ kind }) => {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const orderId = params.get('order');
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    if (!orderId) {
      setStatus(kind === 'success' ? 'paid' : 'failed');
      return undefined;
    }

    let checks = 0;
    let timerId;
    let isMounted = true;

    const pollStatus = async () => {
      try {
        const data = await paymentAPI.getStatus(orderId);

        if (!isMounted) {
          return;
        }

        if (data?.paymentStatus === 'paid') {
          setStatus('paid');
          return;
        }

        if (data?.paymentStatus === 'failed') {
          setStatus('failed');
          return;
        }
      } catch {
        // The payment callback can arrive a moment after the redirect, so retry briefly.
      }

      if (!isMounted) {
        return;
      }

      checks += 1;

      if (checks < MAX_STATUS_CHECKS) {
        timerId = window.setTimeout(pollStatus, STATUS_CHECK_DELAY);
        return;
      }

      setStatus(kind === 'success' ? 'pending' : 'failed');
    };

    pollStatus();

    return () => {
      isMounted = false;
      window.clearTimeout(timerId);
    };
  }, [kind, orderId]);

  if (status === 'checking') {
    return (
      <section className="checkout-result" aria-live="polite">
        <div className="checkout-result__panel">
          <p className="checkout-result__eyebrow">{t('checkout.checking')}</p>
        </div>
      </section>
    );
  }

  if (status === 'paid') {
    return (
      <section className="checkout-result">
        <div className="checkout-result__panel checkout-result__panel--success">
          <p className="checkout-result__eyebrow">{t('checkout.successStrong')}</p>
          <h1>{t('checkout.successText')}</h1>
          <Link className="checkout-result__action" to="/">
            {t('common.home')}
          </Link>
        </div>
      </section>
    );
  }

  if (status === 'pending') {
    return (
      <section className="checkout-result">
        <div className="checkout-result__panel">
          <p className="checkout-result__eyebrow">{t('checkout.processing')}</p>
          {orderId && <p className="checkout-result__order">#{orderId}</p>}
          <Link className="checkout-result__action" to="/">
            {t('common.home')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="checkout-result">
      <div className="checkout-result__panel checkout-result__panel--failed">
        <p className="checkout-result__eyebrow">{t('checkout.failed')}</p>
        {orderId && <p className="checkout-result__order">#{orderId}</p>}
        <Link className="checkout-result__action" to="/user/cart">
          {t('cart.shoppingCart')}
        </Link>
      </div>
    </section>
  );
};

export default CheckoutResult;
